import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  LinearProgress
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  MoreVert,
  CheckCircle,
  ExpandMore
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';

const validationSchema = Yup.object({
  policyName: Yup.string().required('Policy name is required'),
  description: Yup.string().required('Description is required'),
  lateArrivalPenalty: Yup.number()
    .min(0, 'Penalty must be positive')
    .max(100, 'Penalty cannot exceed 100%')
    .required('Late arrival penalty is required'),
  earlyDeparturePenalty: Yup.number()
    .min(0, 'Penalty must be positive')
    .max(100, 'Penalty cannot exceed 100%')
    .required('Early departure penalty is required'),
  overtimeMultiplier: Yup.number()
    .min(1, 'Multiplier must be at least 1')
    .max(3, 'Multiplier cannot exceed 3')
    .required('Overtime multiplier is required'),
  minimumWorkingHours: Yup.number()
    .min(1, 'Minimum hours must be at least 1')
    .max(24, 'Minimum hours cannot exceed 24')
    .required('Minimum working hours is required'),
  requireApprovalForManualPunch: Yup.boolean(),
  requireApprovalForOvertime: Yup.boolean(),
  isActive: Yup.boolean()
});

const AttendancePolicies = () => {
  const { enqueueSnackbar } = useSnackbar();

  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [expandedPolicy, setExpandedPolicy] = useState(null);

  const loadPolicies = useCallback(async () => {
    try {
      setLoading(true);
      // Mock API call - replace with actual API
      const mockPolicies = [
        {
          id: 1,
          policyName: 'Standard Attendance Policy',
          description: 'Standard attendance rules for all employees',
          lateArrivalPenalty: 10,
          earlyDeparturePenalty: 15,
          overtimeMultiplier: 1.5,
          minimumWorkingHours: 8,
          requireApprovalForManualPunch: true,
          requireApprovalForOvertime: true,
          isActive: true,
          applicableGroups: ['All Employees'],
          createdAt: new Date(),
          rules: [
            {
              id: 1,
              ruleType: 'Late Arrival',
              description: 'Deduction of 10% for late arrival',
              isActive: true
            },
            {
              id: 2,
              ruleType: 'Early Departure',
              description: 'Deduction of 15% for early departure',
              isActive: true
            },
            {
              id: 3,
              ruleType: 'Overtime',
              description: '1.5x pay for overtime hours',
              isActive: true
            }
          ]
        },
        {
          id: 2,
          policyName: 'Flexible Work Policy',
          description: 'Flexible attendance policy for remote workers',
          lateArrivalPenalty: 5,
          earlyDeparturePenalty: 5,
          overtimeMultiplier: 1.25,
          minimumWorkingHours: 6,
          requireApprovalForManualPunch: false,
          requireApprovalForOvertime: false,
          isActive: true,
          applicableGroups: ['Remote Workers', 'Contractors'],
          createdAt: new Date(),
          rules: [
            {
              id: 4,
              ruleType: 'Flexible Hours',
              description: 'Flexible start and end times',
              isActive: true
            },
            {
              id: 5,
              ruleType: 'Remote Work',
              description: 'No penalty for remote work arrangements',
              isActive: true
            }
          ]
        }
      ];
      setPolicies(mockPolicies);
    } catch (error) {
      enqueueSnackbar('Failed to load attendance policies', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    loadPolicies();
  }, [loadPolicies]);

  const handleCreatePolicy = async (values) => {
    try {
      const newPolicy = {
        id: Date.now(),
        ...values,
        createdAt: new Date(),
        rules: [],
        applicableGroups: values.applicableGroups || [] // Ensure applicableGroups is always an array
      };
      
      if (editingPolicy) {
        const updatedPolicies = policies.map(p => 
          p.id === editingPolicy.id ? { 
            ...p, 
            ...values,
            applicableGroups: values.applicableGroups || p.applicableGroups || []
          } : p
        );
        setPolicies(updatedPolicies);
        enqueueSnackbar('Policy updated successfully', { variant: 'success' });
      } else {
        setPolicies([...policies, newPolicy]);
        enqueueSnackbar('Policy created successfully', { variant: 'success' });
      }
      
      setOpenDialog(false);
      setEditingPolicy(null);
      formik.resetForm();
    } catch (error) {
      enqueueSnackbar('Failed to save policy', { variant: 'error' });
    }
  };

  const handleEditPolicy = (policy) => {
    setEditingPolicy(policy);
    formik.setValues({
      policyName: policy.policyName,
      description: policy.description,
      lateArrivalPenalty: policy.lateArrivalPenalty,
      earlyDeparturePenalty: policy.earlyDeparturePenalty,
      overtimeMultiplier: policy.overtimeMultiplier,
      minimumWorkingHours: policy.minimumWorkingHours,
      requireApprovalForManualPunch: policy.requireApprovalForManualPunch,
      requireApprovalForOvertime: policy.requireApprovalForOvertime,
      isActive: policy.isActive
    });
    setOpenDialog(true);
  };

  const handleDeletePolicy = async (policyId) => {
    try {
      setPolicies(policies.filter(p => p.id !== policyId));
      enqueueSnackbar('Policy deleted successfully', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Failed to delete policy', { variant: 'error' });
    }
  };

  const formik = useFormik({
    initialValues: {
      policyName: '',
      description: '',
      lateArrivalPenalty: 10,
      earlyDeparturePenalty: 15,
      overtimeMultiplier: 1.5,
      minimumWorkingHours: 8,
      requireApprovalForManualPunch: true,
      requireApprovalForOvertime: true,
      isActive: true
    },
    validationSchema,
    onSubmit: handleCreatePolicy
  });

  const handleMenuOpen = (event, policy) => {
    setAnchorEl(event.currentTarget);
    setSelectedPolicy(policy);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedPolicy(null);
  };

  const getStatusChip = (isActive) => (
    <Chip
      label={isActive ? 'Active' : 'Inactive'}
      color={isActive ? 'success' : 'default'}
      size="small"
    />
  );

  const handleExpandPolicy = (policyId) => {
    setExpandedPolicy(expandedPolicy === policyId ? null : policyId);
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%' }}>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Attendance Policies
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => {
            setEditingPolicy(null);
            formik.resetForm();
            setOpenDialog(true);
          }}
        >
          Create Policy
        </Button>
      </Box>

      {/* Policies List */}
      <Grid container spacing={3}>
        {policies.map((policy) => (
          <Grid item xs={12} key={policy.id}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      {policy.policyName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {policy.description}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      {getStatusChip(policy.isActive)}
                      <Typography variant="caption" color="text.secondary">
                        Created: {new Date(policy.createdAt).toLocaleDateString()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Groups: {policy.applicableGroups && policy.applicableGroups.length > 0 
                          ? policy.applicableGroups.join(', ') 
                          : 'Not assigned'}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleExpandPolicy(policy.id)}
                      endIcon={<ExpandMore />}
                    >
                      {expandedPolicy === policy.id ? 'Hide Details' : 'View Details'}
                    </Button>
                    <IconButton
                      onClick={(e) => handleMenuOpen(e, policy)}
                      size="small"
                    >
                      <MoreVert />
                    </IconButton>
                  </Box>
                </Box>

                {/* Policy Details */}
                {expandedPolicy === policy.id && (
                  <Box>
                    <Divider sx={{ my: 2 }} />
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                          Penalty Settings
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2">Late Arrival Penalty:</Typography>
                            <Typography variant="body2" fontWeight={600}>
                              {policy.lateArrivalPenalty}%
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2">Early Departure Penalty:</Typography>
                            <Typography variant="body2" fontWeight={600}>
                              {policy.earlyDeparturePenalty}%
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2">Overtime Multiplier:</Typography>
                            <Typography variant="body2" fontWeight={600}>
                              {policy.overtimeMultiplier}x
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2">Minimum Working Hours:</Typography>
                            <Typography variant="body2" fontWeight={600}>
                              {policy.minimumWorkingHours} hours
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                          Approval Settings
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CheckCircle 
                              color={policy.requireApprovalForManualPunch ? 'success' : 'disabled'} 
                              fontSize="small" 
                            />
                            <Typography variant="body2">
                              Manual Punch Approval Required
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CheckCircle 
                              color={policy.requireApprovalForOvertime ? 'success' : 'disabled'} 
                              fontSize="small" 
                            />
                            <Typography variant="body2">
                              Overtime Approval Required
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>

                    {/* Rules Section */}
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                        Policy Rules
                      </Typography>
                      {policy.rules.length === 0 ? (
                        <Alert severity="info">
                          No specific rules defined for this policy
                        </Alert>
                      ) : (
                        <Box>
                          {policy.rules.map((rule) => (
                            <Box
                              key={rule.id}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                p: 2,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                                mb: 1
                              }}
                            >
                              <Box sx={{ flexGrow: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {rule.ruleType}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {rule.description}
                                </Typography>
                              </Box>
                              <Chip
                                label={rule.isActive ? 'Active' : 'Inactive'}
                                color={rule.isActive ? 'success' : 'default'}
                                size="small"
                              />
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Create/Edit Policy Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingPolicy ? 'Edit Policy' : 'Create New Policy'}
        </DialogTitle>
        <form onSubmit={formik.handleSubmit}>
          <DialogContent>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Policy Name"
                  name="policyName"
                  value={formik.values.policyName}
                  onChange={formik.handleChange}
                  error={formik.touched.policyName && Boolean(formik.errors.policyName)}
                  helperText={formik.touched.policyName && formik.errors.policyName}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  multiline
                  rows={3}
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  error={formik.touched.description && Boolean(formik.errors.description)}
                  helperText={formik.touched.description && formik.errors.description}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Penalty Settings
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Late Arrival Penalty (%)"
                  name="lateArrivalPenalty"
                  type="number"
                  value={formik.values.lateArrivalPenalty}
                  onChange={formik.handleChange}
                  error={formik.touched.lateArrivalPenalty && Boolean(formik.errors.lateArrivalPenalty)}
                  helperText={formik.touched.lateArrivalPenalty && formik.errors.lateArrivalPenalty}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Early Departure Penalty (%)"
                  name="earlyDeparturePenalty"
                  type="number"
                  value={formik.values.earlyDeparturePenalty}
                  onChange={formik.handleChange}
                  error={formik.touched.earlyDeparturePenalty && Boolean(formik.errors.earlyDeparturePenalty)}
                  helperText={formik.touched.earlyDeparturePenalty && formik.errors.earlyDeparturePenalty}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Overtime Multiplier"
                  name="overtimeMultiplier"
                  type="number"
                  step="0.1"
                  value={formik.values.overtimeMultiplier}
                  onChange={formik.handleChange}
                  error={formik.touched.overtimeMultiplier && Boolean(formik.errors.overtimeMultiplier)}
                  helperText={formik.touched.overtimeMultiplier && formik.errors.overtimeMultiplier}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Minimum Working Hours"
                  name="minimumWorkingHours"
                  type="number"
                  value={formik.values.minimumWorkingHours}
                  onChange={formik.handleChange}
                  error={formik.touched.minimumWorkingHours && Boolean(formik.errors.minimumWorkingHours)}
                  helperText={formik.touched.minimumWorkingHours && formik.errors.minimumWorkingHours}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Approval Settings
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      name="requireApprovalForManualPunch"
                      checked={formik.values.requireApprovalForManualPunch}
                      onChange={formik.handleChange}
                    />
                  }
                  label="Require Approval for Manual Punch"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      name="requireApprovalForOvertime"
                      checked={formik.values.requireApprovalForOvertime}
                      onChange={formik.handleChange}
                    />
                  }
                  label="Require Approval for Overtime"
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      name="isActive"
                      checked={formik.values.isActive}
                      onChange={formik.handleChange}
                    />
                  }
                  label="Active Policy"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editingPolicy ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          handleEditPolicy(selectedPolicy);
          handleMenuClose();
        }}>
          <Edit sx={{ mr: 1 }} />
          Edit Policy
        </MenuItem>
        <MenuItem onClick={() => {
          handleDeletePolicy(selectedPolicy.id);
          handleMenuClose();
        }}>
          <Delete sx={{ mr: 1 }} />
          Delete Policy
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default AttendancePolicies;